/**
 * Route guards — single authority for "who can render this route."
 *
 * EVOLUTION:
 *   Phase 0.5 — extracted from AuthContext.tsx (the original imperative
 *               navigate() approach had race conditions; this version
 *               uses declarative <Navigate> everywhere)
 *   Phase 0.4a — added tenant_owner-without-business redirect to /portfolio
 *   Phase 1 prep — renamed activeBusinessId → activeBusinessSlug to honor
 *                  the backend's slug-based businessMiddleware contract.
 *                  See businessStore.ts for the full rationale.
 *
 * EXPORTED GUARDS:
 *   RequireAuth        — generic "user is logged in" guard, with optional
 *                        role restriction. Also enforces business selection
 *                        for tenant_owners.
 *   NoAuth             — for /login, /register, /forgot-password. Bounces
 *                        already-authenticated users to their home route.
 *   RequireAdmin       — shorthand: tenant_owner + super_admin + business_admin
 *   RequireTenantOwner — shorthand: tenant_owner + super_admin only
 *
 * DESIGN PRINCIPLES:
 *
 *   1. Declarative redirects via <Navigate>, never imperative navigate()
 *      inside useEffect. Reasoning: imperative navigation in render-adjacent
 *      effects causes race conditions when other components also navigate.
 *      <Navigate> is a render-phase decision and React Router handles it
 *      atomically.
 *
 *   2. Single redirect authority. RouteGuards is THE place where "should
 *      this user see this page" gets answered. Components don't redirect.
 *      Pages don't redirect. Only guards redirect.
 *
 *   3. Defensive logging. If a guard makes an unexpected decision (role
 *      not in any known group, missing user object despite isAuthenticated),
 *      the guard returns a safe default (typically a redirect to login)
 *      rather than throwing. We never want a blank page in production.
 */
import { groupScopeFor } from "@/constants/group-mode";
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import useAuthStore, { isAnyAdmin, type UserRole } from "@/stores/authStore";
import useBusinessStore from "@/stores/businessStore";
import { usePermissions } from "@/hooks/usePermissions";

// ─────────────────────────────────────────────────────────────────────
// Role → home route mapping
// ─────────────────────────────────────────────────────────────────────
//
// Single source of truth for "where does this role land by default."
// Used in two scenarios:
//   1. NoAuth redirects logged-in users away from /login etc. to their home
//   2. RequireAuth redirects when allowedRoles doesn't include the user's
//      role (e.g. a cashier hitting /dashboard gets sent to /cashier)
//
// To change a role's home route, edit this map. Don't hardcode route
// strings elsewhere — they should all flow from here.
const ROLE_HOME: Record<UserRole, string> = {
  tenant_owner: "/dashboard",
  super_admin: "/dashboard",
  business_admin: "/dashboard",
  staff: "/dashboard",
  cashier: "/cashier",
};

/**
 * Where to send someone who can't open the page they asked for: the first
 * place they CAN open (permissions decide, custom roles included), never
 * the page itself — so a role without the dashboard can't loop on it.
 */
function homeFor(role: UserRole | undefined, can: (p: string | string[]) => boolean, here: string): string {
  const options: [string, string | string[]][] = [
    ["/dashboard", "report.dashboard.read"],
    ["/cashier", "order.create"],
    ["/deliveries", "dispatch.deliver"],
    ["/van", "van.sell"],
    ["/products", "product.read"],
    ["/stock-expiry", "stock-expiry.report.read"],
    ["/projects", "project.read"],
  ];
  for (const [path, perm] of options) if (path !== here && can(perm)) return path;
  const fallback = homeForRole(role);
  return fallback !== here ? fallback : "/notifications"; // everyone can open their notifications
}

function homeForRole(role: UserRole | undefined): string {
  // Defensive default — unknown role goes to /login. We never assume
  // a missing role means "any role works." Better to force a re-auth
  // than to grant accidental access.
  if (!role) return "/login";
  return ROLE_HOME[role] ?? "/login";
}

// ─────────────────────────────────────────────────────────────────────
// Business-agnostic route allowlist
// ─────────────────────────────────────────────────────────────────────
//
// Paths that a tenant_owner can access WITHOUT having selected an active
// business. Everything else triggers a redirect to /portfolio for the
// "pick your business" flow.
//
// Why this allowlist exists: a tenant_owner with multiple businesses
// has user.businessId = null in their JWT. Until they pick a business
// (which sets businessStore.activeBusinessSlug), the X-Business-Id header
// is missing on every request, and businessMiddleware rejects with 400.
//
// Rather than crash every page, RouteGuards bounces them to /portfolio.
// Some pages are exempt because they don't need business scope:
//
//   /portfolio — the picker itself (would infinite-loop)
//   /settings/profile — managing your own user account, not business data
//   /select-business — alias for /portfolio if we ever rename
//
// Add new entries here carefully. If you add a path that DOES need
// business scope, you'll see 400s from the backend.
const BUSINESS_AGNOSTIC_PATHS = [
  "/portfolio",
  "/settings/profile",
  "/select-business",
  // Group / consolidated views are cross-business by design — they run in
  // "no active business" (Group Mode) and must NOT be bounced to the picker.
  // GroupView even sets activeBusinessSlug = null on mount on purpose.
  "/group",
];

// All businesses mode: Money, Insights, People and Admin pages open without
// choosing a business (constants/group-mode.ts) — company-wide pages as
// they are, one-business pages with their own business bar.
function isBusinessAgnostic(pathname: string): boolean {
  return BUSINESS_AGNOSTIC_PATHS.some((prefix) => pathname.startsWith(prefix)) || groupScopeFor(pathname) !== null;
}

// ─────────────────────────────────────────────────────────────────────
// Brand-aligned loading state
// ─────────────────────────────────────────────────────────────────────
//
// Used while auth state hydrates from localStorage or while initial
// fetchUserProfile is in flight. Uses Eaze brand palette (primary green
// on white background) — never hardcoded blue / hex values.
const AuthLoadingState: React.FC = () => (
  <div className="flex h-screen w-full items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span className="text-sm text-muted-foreground">Loading...</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────
// RequireAuth
// ─────────────────────────────────────────────────────────────────────
//
// The general-purpose authentication guard. Handles four scenarios in
// this order:
//
//   1. Still hydrating → show loading state
//   2. Not authenticated → bounce to /login with the attempted path
//      preserved in state.from (so Login.tsx can return them there)
//   3. Authenticated but role not in allowedRoles → bounce to their home
//   4. tenant_owner without active business AND not on an agnostic path
//      → bounce to /portfolio for business selection
//
// All other cases render children normally.

interface RequireAuthProps {
  children: React.ReactNode;
  /**
   * Restrict to specific roles. Omit to allow any authenticated user.
   * For common cases use the shorthand guards (RequireAdmin,
   * RequireTenantOwner) instead.
   */
  allowedRoles?: UserRole[];
  /**
   * The till (/cashier/*): open to anyone who sells here — cashiers, staff,
   * and managers or owners stepping in — decided by order.create, not by
   * being a "cashier" account.
   */
  till?: boolean;
  /**
   * Who may open this page, by permission (any of them) — custom roles
   * included. When set it replaces allowedRoles: the same permission the
   * server checks on the page's data, so the page and its data agree.
   */
  permission?: string | string[];
}

/**
 * Who they are HERE: the level for the business/branch they're working in
 * (from /permissions/mine) once known, else the account role. Ama — cashier
 * at Osu, supervisor at Tema — gets the till at Osu and the back office at
 * Tema.
 */
export function useEffectiveRole(): UserRole | undefined {
  const user = useAuthStore((s) => s.user);
  const activeBusinessSlug = useBusinessStore((s) => s.activeBusinessSlug);
  const { level, levelKnown } = usePermissions();
  if (!user) return undefined;
  if (activeBusinessSlug && levelKnown && level) return level as UserRole;
  return user.role;
}

/** Staff work in the back office too (what they see is decided by permissions). */
function roleAllowed(role: UserRole, allowed: UserRole[]): boolean {
  return allowed.includes(role) || (role === "staff" && allowed.includes("business_admin"));
}

export const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  allowedRoles,
  till,
  permission,
}) => {
  // Auth slice — what the user IS
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const user = useAuthStore((s) => s.user);

  // Business slice — what business they're currently scoped to
  // (a SLUG, not an ObjectId — see businessStore docstring)
  const activeBusinessSlug = useBusinessStore((s) => s.activeBusinessSlug);

  const location = useLocation();
  const effectiveRole = useEffectiveRole();
  const { can, ready } = usePermissions();
  const mfaSetupRequired = useAuthStore((s) => s.mfaSetupRequired);

  // ── Scenario 1: still hydrating ──────────────────────────────────
  if (isLoading) {
    return <AuthLoadingState />;
  }

  // ── Scenario 2: not authenticated ────────────────────────────────
  // Preserve attempted path so Login.tsx can navigate back after success.
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // From here on, we know isAuthenticated is true. But defensively check
  // for `user` anyway — if isAuthenticated is true but user is null, we
  // have a corrupted state and the safe move is re-auth.
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ── Scenario 3: role restriction ─────────────────────────────────
  // This must come BEFORE the business-scope check. A cashier trying to
  // access an admin route should bounce to their /cashier home, not get
  // sent to /portfolio (which they can't see anyway).
  const role = effectiveRole || user.role;
  if (till && !(role === "cashier" || can("order.create"))) {
    return <Navigate to={homeFor(role, can, location.pathname)} replace />;
  }
  if (permission) {
    if (!ready) return <AuthLoadingState />;
    if (!can(permission)) return <Navigate to={homeFor(role, can, location.pathname)} replace />;
  } else if (allowedRoles && !roleAllowed(role, allowedRoles)) {
    return <Navigate to={homeFor(role, can, location.pathname)} replace />;
  }

  // ── Scenario 3a: the business requires 2-step and it isn't set up ─
  if (mfaSetupRequired) return <Navigate to="/security/two-step" replace />;

  // ── Scenario 3b: drivers and van reps start on their runs ────────
  // Someone without the dashboard who drives or sells from a van (the
  // Driver / Van sales rep roles) lands on their phone page instead.
  if (ready && location.pathname === "/dashboard" && !can("report.dashboard.read")) {
    const runs = can("van.sell") ? "/van" : can("dispatch.deliver") ? "/deliveries" : null;
    if (runs) return <Navigate to={runs} replace />;
  }

  // ── Scenario 4: tenant_owner without active business ─────────────
  // Multi-business tenant_owners must pick a business before any
  // business-scoped request can succeed. /portfolio is the picker.
  // Exempt paths in BUSINESS_AGNOSTIC_PATHS avoid the infinite-loop case
  // (/portfolio redirecting to /portfolio).
  if (
    (user.role === "tenant_owner" || user.role === "super_admin") &&
    !activeBusinessSlug &&
    !isBusinessAgnostic(location.pathname)
  ) {
    // Home in All businesses mode is the group overview; anything that
    // needs one business (selling, stock) goes to the picker.
    return <Navigate to={location.pathname === "/dashboard" ? "/group" : "/portfolio"} replace />;
  }

  // All checks passed — render the route.
  return <>{children}</>;
};

// ─────────────────────────────────────────────────────────────────────
// NoAuth
// ─────────────────────────────────────────────────────────────────────
//
// For routes that should ONLY be visible to logged-out users — /login,
// /register, /forgot-password, /reset-password. If an authenticated user
// hits one of these, bounce them home.
//
// Same tenant_owner-without-business logic as RequireAuth: if they're
// logged in but haven't picked a business yet, send them to /portfolio
// instead of /dashboard. Otherwise the dashboard mounts, fires scoped
// requests, every one fails with 400, and they see a broken page.

interface NoAuthProps {
  children: React.ReactNode;
}

export const NoAuth: React.FC<NoAuthProps> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const activeBusinessSlug = useBusinessStore((s) => s.activeBusinessSlug);

  // Not authenticated → render the public auth page normally
  if (!isAuthenticated || !user) {
    return <>{children}</>;
  }

  // Authenticated tenant_owner without business → /portfolio
  if (user.role === "tenant_owner" && !activeBusinessSlug) {
    return <Navigate to="/portfolio" replace />;
  }

  // Authenticated anyone else → role home
  return <Navigate to={homeForRole(user.role)} replace />;
};

// ─────────────────────────────────────────────────────────────────────
// Convenience guards
// ─────────────────────────────────────────────────────────────────────
//
// Thin wrappers over RequireAuth for the two most common gating
// patterns. Using these in App.tsx route definitions reads more
// naturally than the explicit allowedRoles array:
//
//   <RequireAdmin>          vs   <RequireAuth allowedRoles={[...]}>
//   <RequireTenantOwner>    vs   <RequireAuth allowedRoles={["tenant_owner", "super_admin"]}>
//
// When in doubt, prefer the explicit allowedRoles form — it makes the
// intent clear in code review.

/**
 * Admin-level access — tenant_owner, super_admin, business_admin.
 * Use for routes that modify business data: product CRUD, user mgmt,
 * approve expenditures, etc.
 */
export const RequireAdmin: React.FC<{ children: React.ReactNode; permission?: string | string[] }> = ({
  children,
  permission,
}) => (
  <RequireAuth allowedRoles={["tenant_owner", "super_admin", "business_admin"]} permission={permission}>
    {children}
  </RequireAuth>
);

/**
 * Tenant-wide access — tenant_owner, super_admin only.
 * Use for routes that affect company-level settings or operate across
 * multiple businesses: billing, subscription mgmt, portfolio overview,
 * editing completed orders (Phase F).
 */
export const RequireTenantOwner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <RequireAuth allowedRoles={["tenant_owner", "super_admin"]}>
    {children}
  </RequireAuth>
);

// ─────────────────────────────────────────────────────────────────────
// Helpers re-exported for components that need to render conditionally
// on role (NOT route-gate). For route gating, always use a guard above.
// ─────────────────────────────────────────────────────────────────────
export { isAnyAdmin };
